import { useState, useRef, useEffect, CSSProperties } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { SpanningTaskSegment, MONTH_VIEW_SPANNING_ROW_HEIGHT, SPANNING_EVENT_GAP, MONTH_VIEW_DAY_HEADER_HEIGHT } from "./spanningEventHelpers"
import {
  getSpanningTaskOuterStyles,
  getSpanningTaskInnerStyle,
  SpanningTaskContent,
  SpanningTaskResizeHandle,
} from "./spanningTaskCardParts"
import { TaskCalendarEvent } from "../scheduleApi"
import TaskPopoverContent from "../TaskPopoverContent"
import { HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"

export type Props = {
  segment: SpanningTaskSegment
  onTaskClick: (event: TaskCalendarEvent) => void
  onResize?: (task: TaskCalendarEvent, newEndDate: Dayjs) => void
  onResizingChange?: (isResizing: boolean) => void
  weekStart: Dayjs
}

const UNASSIGNED_COLOR = '#e0e0e0'

const MonthViewSpanningTask = ({ segment, onTaskClick, onResize, onResizingChange, weekStart }: Props) => {
  const { task, startDayIndex, spanWidth, isStart, isEnd, rowIndex, weekIndex } = segment
  const color = task.crewForemanColor || UNASSIGNED_COLOR
  const isCompleted = !!task.completedAt
  const [isHovering, setIsHovering] = useState(false)
  const [resizeTargetEpoch, setResizeTargetEpoch] = useState<number | null>(null)
  const justFinishedResizing = useRef(false)
  const resizeTargetEpochRef = useRef<number | null>(null)

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `spanning-task-${task.id}-${weekIndex}`,
    data: { event: task, segment, isTask: true },
    disabled: task.frozen,
  })

  const resizeId = `spanning-task-${task.id}-${weekIndex}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
  } = useDraggable({
    id: resizeId,
    data: { event: task, segment, isResize: true, isTask: true },
    disabled: task.frozen,
  })

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragOver: (dragOverEvent) => {
      if (dragOverEvent.active.id !== resizeId) {
        return
      }
      const overDate = dragOverEvent.over?.data.current?.date as Dayjs | undefined
      if (overDate) {
        const epoch = overDate.valueOf()
        resizeTargetEpochRef.current = epoch
        setResizeTargetEpoch(epoch)
      }
    },
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      justFinishedResizing.current = true

      const targetEpoch = resizeTargetEpochRef.current
      if (onResize && targetEpoch !== null) {
        const targetDate = dayjs(targetEpoch)
        const taskStart = dayjs(task.start)
        if (!targetDate.isBefore(taskStart, 'day') && !targetDate.isSame(dayjs(task.end), 'day')) {
          onResize(task, targetDate)
        }
      }

      resizeTargetEpochRef.current = null
      setResizeTargetEpoch(null)
    },
  })

  const leftPercent = (startDayIndex / 7) * 100
  let widthPercent = (spanWidth / 7) * 100

  if (isResizing && resizeTargetEpoch !== null) {
    const targetDate = dayjs(resizeTargetEpoch)
    const taskStart = dayjs(task.start)
    const targetDayIndex = targetDate.diff(weekStart.startOf('day'), 'day')
    const minEndDayIndex = Math.max(startDayIndex, taskStart.diff(weekStart.startOf('day'), 'day'))
    const newEndDayIndex = Math.max(targetDayIndex, minEndDayIndex)

    const clampedEndDayIndex = Math.min(newEndDayIndex, 6)
    const newSpanWidth = clampedEndDayIndex - startDayIndex + 1
    widthPercent = (newSpanWidth / 7) * 100
  }

  const top = MONTH_VIEW_DAY_HEADER_HEIGHT + rowIndex * (MONTH_VIEW_SPANNING_ROW_HEIGHT + SPANNING_EVENT_GAP)

  const paddingLeft = isStart ? 2 : 0
  const paddingRight = isEnd ? 2 : 0

  const outerStyle: CSSProperties = {
    ...getSpanningTaskOuterStyles({ color, isStart, isEnd }),
    position: "absolute",
    top,
    left: `calc(${leftPercent}% + ${paddingLeft}px)`,
    width: `calc(${widthPercent}% - ${paddingLeft + paddingRight}px)`,
    height: MONTH_VIEW_SPANNING_ROW_HEIGHT,
    cursor: isResizing ? "ew-resize" : isDragging ? "grabbing" : "pointer",
    zIndex: isDragging || isResizing ? 100 : 1,
    transform: !isResizing && transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
  }

  const innerStyle = getSpanningTaskInnerStyle({ isEnd })

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  const contentElements = (
    <SpanningTaskContent
      task={task}
      isStart={isStart}
      isEnd={isEnd}
      isResizing={isResizing}
      isCompleted={isCompleted}
      titleFontSize={12}
    />
  )

  return (
    <Popover
      content={<TaskPopoverContent event={task} />}
      open={isHovering && !isDragging && !isResizing}
      placement="bottom"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (justFinishedResizing.current) {
            justFinishedResizing.current = false
            return
          }
          if (!isResizing) {
            onTaskClick(task)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={outerStyle}
      >
        {isStart ? <div style={innerStyle}>{contentElements}</div> : contentElements}

        {isEnd && (isHovering || isResizing) && onResize && (
          <SpanningTaskResizeHandle
            setNodeRef={setResizeNodeRef}
            listeners={resizeListeners}
            attributes={resizeAttributes}
            color={color}
          />
        )}
      </div>
    </Popover>
  )
}

export default MonthViewSpanningTask
